import { User } from "../../../features/user/types";

/**
 * Tooltip shown when a status option is disabled because the user lacks
 * permission to move a post into it. Shared across surfaces (status dropdown,
 * Approvals Board drag-and-drop, context menu) so the message stays consistent.
 */
export const STATUS_CHANGE_NOT_ALLOWED_TOOLTIP =
  "You are not allowed to change this post status.";

/**
 * Tooltip shown when a non-publishable status option is disabled because the
 * post-statuses (approvals) feature is not available on the current plan.
 * The select itself stays enabled so the user can still move the post to a
 * publishable status (e.g. after downgrading) rather than being stuck.
 */
export const STATUS_CHANGE_UPSELL_TOOLTIP =
  "This status isn't available on your current plan. Upgrade to use approval statuses.";

/**
 * Whether `user` is permitted to move a post into the approval status
 * identified by `statusKey`.
 *
 * Mirrors the create-post flow: collaborators are restricted to the statuses
 * listed in `user.preferences.canChangeStatusTo`; admins/owners may use any
 * status. Extracted so the board's drop-target validation and context-menu
 * gating stay in lockstep with the status dropdown.
 */
export const canChangeToStatus = (
  user: Pick<User, "role" | "preferences">,
  statusKey: string
): boolean => {
  const allowedStatuses = user.preferences?.canChangeStatusTo;
  return (
    user.role !== "collaborator" ||
    (Array.isArray(allowedStatuses) && allowedStatuses.includes(statusKey))
  );
};

/**
 * Whether `user` is permitted to move an existing post from its current status
 * into `targetStatusKey`. Empty legacy source statuses are not source-gated,
 * but the destination still must be allowed for collaborators.
 */
export const canChangeStatusFromTo = (
  user: Pick<User, "role" | "preferences">,
  currentStatusKey: string | null | undefined,
  targetStatusKey: string
): boolean => {
  return (
    canChangeToStatus(user, targetStatusKey) &&
    (!currentStatusKey || canChangeToStatus(user, currentStatusKey))
  );
};
